import styled from "@emotion/styled"
import { colorContrastLow, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { DatePrice } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { CheckboxField, FormLayout, InputField } from "src/components/form"
import { InputNumber } from "src/components/form/inputNumber"
import { GridItem } from "src/components/grid/GridItem"
import { gridSize } from "../../../common/gridSize.const"
import type { InventoryInformationFormValues } from "../../../inventoryAndIntakeForm/InventoryInformationFormValues"
import { PricingFormGrid } from "../../components/PricingFormGrid"
import { PriceHistory } from "../../components/priceHistory/PriceHistory"
import { pricingFieldMessages } from "../../hooks/pricingField.messages"
import type { JapanVehicleAndMiscPricingFields } from "../japanVehicleAndMiscPricing.fields"

export interface JapanVehicleAndMiscPricingFormProps {
	fields: JapanVehicleAndMiscPricingFields
	priceHistory: DatePrice[] | undefined
}

export const JapanVehicleAndMiscPricingForm = ({ fields, priceHistory }: JapanVehicleAndMiscPricingFormProps) => {
	const { formatMessage } = useIntl()

	const { watch } = useFormContext<InventoryInformationFormValues>()
	const vehiclePrice = watch("vehiclePrice")
	const automobileTax = watch("automobileTax")
	const tonnageTax = watch("tonnageTax")
	const lightVehicleTaxEnvironmentalPerformanceLevy = watch("lightVehicleTaxEnvironmentalPerformanceLevy")
	const liabilityInsurance = watch("liabilityInsurance")
	const recyclingFee = watch("recyclingFee")
	const inspectionRegistration = watch("inspectionRegistration")
	const inspectionRegistrationAgencyFee = watch("inspectionRegistrationAgencyFee")
	const parkingCertificate = watch("parkingCertificate")
	const parkingCertificationAcquisitionAgencyFee = watch("parkingCertificationAcquisitionAgencyFee")

	const totalPrice = useMemo(() => {
		return (
			(vehiclePrice ?? 0) +
			(automobileTax ?? 0) +
			(tonnageTax ?? 0) +
			(lightVehicleTaxEnvironmentalPerformanceLevy ?? 0) +
			(liabilityInsurance ?? 0) +
			(recyclingFee ?? 0) +
			(inspectionRegistration ?? 0) +
			(inspectionRegistrationAgencyFee ?? 0) +
			(parkingCertificate ?? 0) +
			(parkingCertificationAcquisitionAgencyFee ?? 0)
		)
	}, [
		vehiclePrice,
		automobileTax,
		tonnageTax,
		lightVehicleTaxEnvironmentalPerformanceLevy,
		liabilityInsurance,
		recyclingFee,
		inspectionRegistration,
		inspectionRegistrationAgencyFee,
		parkingCertificate,
		parkingCertificationAcquisitionAgencyFee,
	])

	return (
		<PricingFormGrid>
			<GridItemFormLayout size={gridSize}>
				<InputNumber
					name="customerPrice"
					label={formatMessage(pricingFieldMessages.customerPriceLabel)}
					type="currency"
					value={totalPrice}
					readOnly
				/>
				<VatDeductibleCheckboxField {...fields.vatDeductible} />
				<InputField {...fields.vehiclePrice} />
				<InputField {...fields.automobileTax} />
				<InputField {...fields.tonnageTax} />
				<InputField {...fields.lightVehicleTaxEnvironmentalPerformanceLevy} />
				<InputField {...fields.liabilityInsurance} />
				<InputField {...fields.recyclingFee} />
				<InputField {...fields.inspectionRegistration} />
				<InputField {...fields.inspectionRegistrationAgencyFee} />
				<InputField {...fields.parkingCertificate} />
				<InputField {...fields.parkingCertificationAcquisitionAgencyFee} />
				<InputFieldWithDivider {...fields.minimumPrice} />
				<InputField {...fields.purchasePrice} />
			</GridItemFormLayout>

			{priceHistory && priceHistory.length > 0 && (
				<GridItem size={gridSize}>
					<PriceHistory view={priceHistory} />
				</GridItem>
			)}
		</PricingFormGrid>
	)
}

const StyledFormLayout = styled(FormLayout)`
	align-self: start; /* needed when price history is larger than form, otherwise gaps between inputs increase */
`

const GridItemFormLayout = StyledFormLayout.withComponent(GridItem)

const InputFieldWithDivider = styled(InputField)`
	padding-top: ${spacingStaticMd};
	border-top: 1px solid ${colorContrastLow};
`

const VatDeductibleCheckboxField = styled(CheckboxField)`
	margin-bottom: ${spacingStaticMd};
`
